/**
 * Labels.
 *
 * The designer picks a size and decides what appears; the print list says how
 * many of each item. Printing goes through the browser at the exact label size,
 * which is what makes Arabic come out with its letters joined - thermal printers
 * in text mode usually get that wrong.
 *
 * Printing never needs the internet: the label printer is plugged into the
 * device, and the fonts are already on it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CatalogItem } from '@pos/shared';
import { useI18n, useT } from '../../core/i18n/index.js';
import { useSettings } from '../../core/state/settings.js';
import { useToast } from '../../core/state/toast.js';
import { api } from '../../core/api.js';
import { searchItems } from '../../core/db.js';
import { DEFAULT_LABEL_CONFIG, printLabels, type LabelConfig, type LabelItem } from '../../core/print.js';
import { Button, Empty, Field, Modal, Pill, Swatch } from '../../core/ui/ui.js';
import { Barcode } from '../../core/ui/bits.js';

interface Template {
  id: string;
  name: string;
  width_mm: number;
  height_mm: number;
  config: LabelConfig;
  isDefault: boolean;
}

const FALLBACK: Template[] = [
  { id: 'a', name: '40 × 30 mm', width_mm: 40, height_mm: 30, config: DEFAULT_LABEL_CONFIG, isDefault: true },
  { id: 'b', name: '50 × 25 mm', width_mm: 50, height_mm: 25, config: DEFAULT_LABEL_CONFIG, isDefault: false },
];

export function useTemplates() {
  const [templates, setTemplates] = useState<Template[]>(FALLBACK);
  useEffect(() => {
    void api<Template[]>('/catalog/label-templates')
      .then((rows) => rows.length && setTemplates(rows))
      .catch(() => undefined);
  }, []);
  return templates;
}

/** Turn a catalogue item into what actually gets printed on a tag. */
export function toLabelItem(i: CatalogItem): LabelItem {
  return {
    nameEn: i.nameEn,
    nameAr: i.nameAr,
    size: i.size,
    colorEn: i.colorEn,
    colorAr: i.colorAr,
    price: i.price,
    barcode: i.barcodes[0] ?? i.sku,
  };
}

// ---------------------------------------------------------------------------
// The print dialog, opened from a product, from receiving, or from Labels
// ---------------------------------------------------------------------------

export function LabelPrinter({
  items,
  quantities,
  onClose,
}: {
  items: CatalogItem[];
  /** Pre-filled counts, e.g. exactly what was just received. */
  quantities?: Record<string, number>;
  onClose: () => void;
}) {
  const t = useT();
  const { lang } = useI18n();
  const { settings } = useSettings();
  const templates = useTemplates();
  const [templateId, setTemplateId] = useState('');
  const [qty, setQty] = useState<Record<string, number>>(quantities ?? {});
  const [config, setConfig] = useState<LabelConfig>(DEFAULT_LABEL_CONFIG);
  const [size, setSize] = useState({ widthMm: 40, heightMm: 30 });

  useEffect(() => {
    const tpl = templates.find((x) => x.isDefault) ?? templates[0];
    if (tpl) {
      setTemplateId(tpl.id);
      setConfig({ ...DEFAULT_LABEL_CONFIG, ...tpl.config });
      setSize({ widthMm: tpl.width_mm, heightMm: tpl.height_mm });
    }
  }, [templates]);

  const total = useMemo(() => Object.values(qty).reduce((a, b) => a + (b || 0), 0), [qty]);
  const storeName = lang === 'ar' ? settings.storeNameAr : settings.storeNameEn;
  const preview = items[0] ? toLabelItem(items[0]) : null;

  const print = () => {
    const list = items
      .filter((i) => (qty[i.variantId] ?? 0) > 0)
      .map((i) => ({ item: toLabelItem(i), qty: qty[i.variantId] }));
    void printLabels(list, config, size, storeName);
  };

  const toggles: { key: keyof LabelConfig; label: string }[] = [
    { key: 'showStoreName', label: t('labels.showStoreName') },
    { key: 'showNameAr', label: t('labels.showNameAr') },
    { key: 'showNameEn', label: t('labels.showNameEn') },
    { key: 'showSize', label: t('labels.showSize') },
    { key: 'showColor', label: t('labels.showColour') },
    { key: 'showPrice', label: t('labels.showPrice') },
    { key: 'showBarcodeText', label: t('labels.showBarcodeText') },
  ];

  return (
    <Modal
      title={t('labels.title')}
      wide
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <div className="spacer" />
          <Button variant="primary" disabled={total === 0} onClick={print}>
            {t('labels.printAll', { n: total })}
          </Button>
        </>
      }
    >
      <div className="grid c2" style={{ alignItems: 'start' }}>
        <div className="col">
          <Field label={t('labels.template')}>
            <select
              value={templateId}
              onChange={(e) => {
                const tpl = templates.find((x) => x.id === e.target.value);
                setTemplateId(e.target.value);
                if (tpl) {
                  setConfig({ ...DEFAULT_LABEL_CONFIG, ...tpl.config });
                  setSize({ widthMm: tpl.width_mm, heightMm: tpl.height_mm });
                }
              }}
            >
              {templates.map((x) => (
                <option key={x.id} value={x.id}>{x.name}</option>
              ))}
            </select>
          </Field>

          <div className="grid c2">
            <Field label={t('labels.width')}>
              <input
                type="number"
                min={15}
                max={120}
                value={size.widthMm}
                onChange={(e) => setSize({ ...size, widthMm: Number(e.target.value) })}
              />
            </Field>
            <Field label={t('labels.height')}>
              <input
                type="number"
                min={10}
                max={120}
                value={size.heightMm}
                onChange={(e) => setSize({ ...size, heightMm: Number(e.target.value) })}
              />
            </Field>
          </div>

          <div>
            <label style={{ fontSize: 'var(--t-xs)', fontWeight: 600 }}>{t('labels.whatToShow')}</label>
            {toggles.map((x) => (
              <label key={x.key} className="checkline">
                <input
                  type="checkbox"
                  checked={!!config[x.key]}
                  onChange={(e) => setConfig({ ...config, [x.key]: e.target.checked })}
                />
                {x.label}
              </label>
            ))}
          </div>

          <p className="xsmall muted">{t('labels.printHint')}</p>
        </div>

        <div className="col">
          <label style={{ fontSize: 'var(--t-xs)', fontWeight: 600 }}>{t('labels.preview')}</label>
          <div className="label-preview">
            {preview ? (
              <div
                className="label"
                style={{ width: `${size.widthMm}mm`, height: `${size.heightMm}mm` }}
              >
                {config.showStoreName ? (
                  <div style={{ fontSize: `${6 * config.fontScale}pt`, fontWeight: 600 }}>{storeName}</div>
                ) : null}
                {config.showNameAr ? (
                  <div className="nm" style={{ fontSize: `${7.5 * config.fontScale}pt` }}>{preview.nameAr}</div>
                ) : null}
                {config.showNameEn ? (
                  <div className="nm" style={{ fontSize: `${7 * config.fontScale}pt` }}>{preview.nameEn}</div>
                ) : null}
                <div className="row" style={{ fontSize: `${7 * config.fontScale}pt` }}>
                  {config.showSize ? <span className="sz">{preview.size}</span> : null}
                  {config.showColor ? <span>{preview.colorAr || preview.colorEn}</span> : null}
                  {config.showPrice ? (
                    <span className="pr" style={{ fontSize: `${9.5 * config.fontScale}pt` }}>
                      ₪{(preview.price / 100).toFixed(2)}
                    </span>
                  ) : null}
                </div>
                <Barcode
                  value={preview.barcode}
                  height={Math.max(14, size.heightMm * 0.9)}
                  width={1.15}
                  showText={config.showBarcodeText}
                  fontSize={8 * config.fontScale}
                />
              </div>
            ) : null}
          </div>

          <label style={{ fontSize: 'var(--t-xs)', fontWeight: 600 }}>{t('labels.howMany')}</label>
          <div className="table-wrap" style={{ maxHeight: 260, overflowY: 'auto' }}>
            <table>
              <tbody>
                {items.map((i) => (
                  <tr key={i.variantId}>
                    <td>
                      <div className="small strong">{i.size}</div>
                      <div className="xsmall muted">
                        <Swatch hex={i.colorHex} /> {lang === 'ar' ? i.colorAr : i.colorEn}
                      </div>
                    </td>
                    <td className="num" style={{ width: 110 }}>
                      <input
                        type="number"
                        min={0}
                        value={qty[i.variantId] ?? 0}
                        onChange={(e) => setQty({ ...qty, [i.variantId]: Math.max(0, Number(e.target.value)) })}
                        style={{ width: 80, textAlign: 'center' }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="btn-row">
            <Button size="sm" onClick={() => setQty(Object.fromEntries(items.map((i) => [i.variantId, 1])))}>
              1 ×
            </Button>
            <Button
              size="sm"
              onClick={() => setQty(Object.fromEntries(items.map((i) => [i.variantId, Math.max(0, i.stock)])))}
            >
              {t('common.stock')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setQty({})}>
              {t('common.clear')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// The Labels screen: search anything, build a print list
// ---------------------------------------------------------------------------

export function Labels() {
  const t = useT();
  const { lang } = useI18n();
  const { money } = useSettings();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<CatalogItem[]>([]);
  const [queue, setQueue] = useState<CatalogItem[]>([]);
  const [printing, setPrinting] = useState(false);

  const search = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    try {
      setResults(await api<CatalogItem[]>(`/catalog/search?q=${encodeURIComponent(query)}`));
    } catch {
      setResults(await searchItems(query, 40));
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => void search(q), 200);
    return () => clearTimeout(timer);
  }, [q, search]);

  const add = (i: CatalogItem) => {
    if (queue.some((x) => x.variantId === i.variantId)) return;
    setQueue((cur) => [...cur, i]);
    toast.ok(t('labels.addToQueue'));
  };

  const nm = (i: CatalogItem) => (lang === 'ar' ? i.nameAr || i.nameEn : i.nameEn || i.nameAr);

  return (
    <div className="grid c2" style={{ alignItems: 'start' }}>
      <div className="card">
        <h2>{t('common.search')}</h2>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('common.searchPlaceholder')} aria-label={t('common.searchPlaceholder')}
          style={{ marginBlock: 'var(--s-3)' }}
          data-scan-target="true"
        />
        <div className="col" style={{ gap: 'var(--s-1)', maxHeight: '55vh', overflowY: 'auto' }}>
          {results.map((r) => (
            <button key={r.variantId} className="pick-row" onClick={() => add(r)}>
              <span style={{ flex: 1, textAlign: 'start' }}>
                <span className="strong">{nm(r)}</span>
                <span className="xsmall muted"> · {r.size} · {lang === 'ar' ? r.colorAr : r.colorEn}</span>
              </span>
              <span className="tnum">{money(r.price)}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>{t('labels.queue')}</h2>
          <div className="spacer" />
          <Pill>{queue.length}</Pill>
        </div>

        {queue.length === 0 ? (
          <Empty icon="🏷️" title={t('labels.empty')} hint={t('labels.emptyHint')} />
        ) : (
          <>
            <div className="col" style={{ gap: 'var(--s-1)', maxHeight: '45vh', overflowY: 'auto' }}>
              {queue.map((i) => (
                <div key={i.variantId} className="ret-row">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="small strong">{nm(i)}</div>
                    <div className="xsmall muted">
                      {i.size} · {lang === 'ar' ? i.colorAr : i.colorEn} · {i.barcodes[0]}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={t('checkout.removeLine')}
                    onClick={() => setQueue((c) => c.filter((x) => x.variantId !== i.variantId))}
                  >
                    ✕
                  </Button>
                </div>
              ))}
            </div>
            <Button
              variant="primary"
              size="lg"
              block
              style={{ marginBlockStart: 'var(--s-4)' }}
              onClick={() => setPrinting(true)}
            >
              {t('labels.designer')}
            </Button>
          </>
        )}
      </div>

      {printing ? <LabelPrinter items={queue} onClose={() => setPrinting(false)} /> : null}
    </div>
  );
}
